import { DateTime } from 'luxon';

import { DischargePlanStatuses } from '~/components/maud/patients/profile/tabs/careplan/dischargeplan/common/dischargePlanConstants';

import { PATIENT_VIEWS, PatientViewKey } from './constants';

export const isValidPatientView = (value: unknown): value is PatientViewKey =>
  typeof value === 'string' &&
  (Object.values(PATIENT_VIEWS) as string[]).includes(value);

type BadgeTone = 'red' | 'amber' | null;

interface TimeElapsed {
  label: string;
  tone: BadgeTone;
}

export const getTimeElapsed = (createdAt: string): TimeElapsed => {
  const hours = Math.max(
    0,
    DateTime.now().diff(DateTime.fromISO(createdAt)).as('hours'),
  );
  if (hours >= 48) {
    return { label: `${Math.floor(hours / 24)} days ago`, tone: 'red' };
  }
  if (hours >= 24) {
    return { label: '1 day ago', tone: 'amber' };
  }
  if (hours < 1) {
    return { label: 'Just now', tone: null };
  }
  return { label: `${Math.floor(hours)} hr ago`, tone: null };
};

export const getGreeting = (): string => {
  const hour = DateTime.now().hour;
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

export const getLastUpdatedLabel = (minutesAgo: number): string => {
  if (minutesAgo < 1) return 'Last updated just now';
  if (minutesAgo === 1) return 'Last updated 1 minute ago';
  return `Last updated ${minutesAgo} minutes ago`;
};

export const formatPauseSince = (startDate: string): string =>
  startDate ? DateTime.fromISO(startDate).toFormat('LLL d') : '';

export const getDaysSinceLabel = (startDate: string): string => {
  if (!startDate) return '';
  const days = Math.max(
    0,
    Math.floor(DateTime.now().diff(DateTime.fromISO(startDate), 'days').days),
  );
  return `${days} day${days === 1 ? '' : 's'}`;
};

export const getPatientLabel = (count?: number): string => {
  return count === 1 ? 'patient' : 'patients';
};

export const formatDischargeSessionDate = (iso: string): string => {
  const dt = DateTime.fromISO(iso);
  return dt.isValid ? dt.toFormat('LLL d') : '';
};

export const getDischargeRelativeLabel = (iso: string): string => {
  const dt = DateTime.fromISO(iso);
  if (!dt.isValid) return '';
  const days = Math.round(
    dt.startOf('day').diff(DateTime.now().startOf('day'), 'days').days,
  );
  if (days < 0) return '';
  if (days === 0) return '(today)';
  if (days === 1) return '(tomorrow)';
  return `(in ${days} days)`;
};

export const formatAuthEndDate = (date: string): string => {
  const dt = DateTime.fromISO(date);
  return dt.isValid ? dt.toFormat('L/d/yyyy') : '';
};

export const getDischargePlanLabel = (status?: string | null): string => {
  if (status === DischargePlanStatuses.SIGNED.code)
    return 'Signed discharge plan';
  if (status == null) return 'No discharge plan';
  return 'Draft discharge plan';
};

export const formatAppointmentTimeRange = (
  startIso: string,
  durationMinutes: number,
): string => {
  const start = DateTime.fromISO(startIso);
  if (!start.isValid) return '';
  const end = start.plus({ minutes: durationMinutes });
  const label = (dt: DateTime) => dt.toFormat('h:mm').replace(':00', '');
  const meridiem = (dt: DateTime) => dt.toFormat('a').toLowerCase();
  return meridiem(start) === meridiem(end)
    ? `${label(start)} - ${label(end)} ${meridiem(start)}`
    : `${label(start)} ${meridiem(start)} - ${label(end)} ${meridiem(end)}`;
};
